import React, { FC } from 'react';
import styled from '@emotion/styled';
import { getTextColor } from '../../style';
import GalleryModal from '../../modules/modals/GalleryModal';
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll';
import PrimaryButton from '../../atoms/buttons/PrimaryButton';
import useBreakpointQuery from '../../hooks/useBreakPointQuery';
import { CustomStyledProps } from 'style/style';
import { isDarkMode } from 'utils/helpers';

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const ModalHeader = styled('h3')(() => ({
  marginTop: 30,
  fontWeight: 600,
  fontSize: 18,
  lineHeight: '23px',
  letterSpacing: '-0.5px',
  textAlign: 'left',
  marginBottom: 11,
  alignSelf: 'baseline',
  color: isDarkMode() ? '#F6F4F4' : '#212121',
}));

const ModalDescription = styled('p')({
  fontSize: 14,
  lineHeight: '19px',
  letterSpacing: '-0.3px',
  marginTop: 0,
  marginBottom: 0,
});

const ModalDescriptionWrapper = styled('div')(
  () => ({
    paddingBottom: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    flexDirection: 'column',
    maxWidth: 400,
    ...getTextColor().DARK,
  }),
  ({ hasHeader }: CustomStyledProps) => !hasHeader && { marginTop: 0 }
);

const SkipButton = styled('a')(() => ({
  color: isDarkMode() ? '#CAE3F5' : '#8392A7',
  fontSize: 15,
  fontWeight: 400,
  lineHeight: '17px',
  marginTop: 12,
  cursor: 'pointer',
}));

interface JoinSkipConfirmationProps {
  onClose(): void;
  onGoBack(): void;
  onSkip(): void;
  isOpen: boolean;
  alwaysDisabledBodyScroll?: boolean;
  customStyleSkipButton?: React.CSSProperties;
}

const JoinSkipConfirmation: FC<JoinSkipConfirmationProps> = ({
  onClose,
  onGoBack,
  onSkip,
  isOpen,
  alwaysDisabledBodyScroll = false,
  customStyleSkipButton,
}) => {
  useDisableBodyScroll(alwaysDisabledBodyScroll || isOpen, alwaysDisabledBodyScroll);
  const isBreakpointDown359 = useBreakpointQuery({ key: 'down', value: 359 });

  return (
    <GalleryModal
      onClose={onClose}
      customStyle={{ content: { maxWidth: 400, paddingBottom: 0 } }}
      customContentStyle={{ padding: 30 }}
      contentLabel=""
      isOpen={isOpen}
      customCloseBtnStyle={{ top: 17, right: 17 }}
      forceDisableBodyScroll={alwaysDisabledBodyScroll}
    >
      <ModalDescriptionWrapper hasHeader={true} data-test-id="gallery-agreement">
        <ModalHeader>Are you sure you want to skip?</ModalHeader>
        <ModalDescription>
          {`Skipping this step means you won't be able to send or receive date offers nor add people to Favorites.`}
        </ModalDescription>

        <div style={{ marginTop: 30 }} data-test-id="always-show-agreement"></div>
        <PrimaryButton
          onClick={onGoBack}
          size="regular"
          label="No, go back and finish"
          color="red"
          customStyle={{
            padding: isBreakpointDown359 ? '12px 22px' : '12px 32px',
            width: 'inherit',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '16px',
            letterSpacing: '-0.32px',
            height: 'auto',
          }}
        />
        <SkipButton data-test-id="join-modal-skip" onClick={onSkip} style={customStyleSkipButton}>
          Yes, skip for now
        </SkipButton>
      </ModalDescriptionWrapper>
    </GalleryModal>
  );
};

export default JoinSkipConfirmation;
